import React from 'react';

import { mountRootParcel } from 'single-spa';
import Parcel from 'single-spa-react/parcel';

import { createReactRenderer } from 'helpers/react-renderer';
import { InvoicingConfig } from 'root/apps/QuoteMachineIntegration/common';
import { CustomerInvoicesListWebComponentHost } from 'root/apps/QuoteMachineIntegration/components/CustomerProfileWebComponents';
import CustomerDepositService from 'root/apps/QuoteMachineIntegration/services/customerDepositService';
import CustomerInvoiceService from 'root/apps/QuoteMachineIntegration/services/customerInvoiceService';
import InvoiceService from 'root/apps/QuoteMachineIntegration/services/InvoiceService';
import { CustomerInvoicesListProps } from 'root/apps/QuoteMachineIntegration/state/CustomerInvoicesListProps';
import { SystemJS } from 'root/apps/QuoteMachineIntegration/SystemJS';
import { hasCreditAccountId } from 'root/apps/QuoteMachineIntegration/utils/creditAccountId';
import { getSessionData } from 'root/apps/QuoteMachineIntegration/utils/SessionData';
import Modules from 'root/apps/shared_modules/Modules';
import { datadogRum } from 'root/datadog/datadogRum';

const CustomerInvoicesList = ({ customerId, creditAccountId }: CustomerInvoicesListProps) => {
  if (
    Modules.hasModule('customers.invoicing_integration.customer_profile_web_components') &&
    hasCreditAccountId(creditAccountId)
  ) {
    return (
      <CustomerInvoicesListWebComponentHost
        accountID={String(window.merchantos.account.id)}
        customerId={customerId}
        creditAccountId={creditAccountId}
      />
    );
  }

  const handleViewInvoice = async (proposalLink: string, invoiceId: string) => {
    if (!proposalLink || !invoiceId) return;

    try {
      const merchantToken = await InvoiceService.getProposalToken(invoiceId);
      const url = merchantToken
        ? `${proposalLink}?merchantToken=${encodeURIComponent(merchantToken)}`
        : proposalLink;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      datadogRum.addError(error, {
        message: `Failed to get merchant token for invoice ${invoiceId}`,
        invoiceId,
        proposalLink,
      });
      window.open(proposalLink, '_blank', 'noopener,noreferrer');
    }
  };

  const fetchInvoices = (params: { page: number; limit: number }) =>
    CustomerInvoiceService.fetchCustomerInvoices(creditAccountId, params);

  const fetchDeposits = (params: { page: number; limit: number }) =>
    CustomerDepositService.fetchCustomerDeposits(creditAccountId, params);

  const handleViewTransaction = (transactionRef: string) => {
    if (!transactionRef) return;

    // Extract transaction ID from transactionRef (e.g., "ls-retail_402" -> "402")
    const transactionId = transactionRef.replace(/^ls-retail_/, '');
    if (!transactionId) return;

    window.showNotifier('working');

    // Construct the transaction view URL
    const baseUrl = window.location.origin;
    const transactionUrl = `${baseUrl}/?name=transaction.views.transaction&form_name=view&id=${transactionId}&tab=details`;

    // Open the transaction view in a new tab
    window.open(transactionUrl, '_blank', 'noopener,noreferrer');
    window.hideNotifier('working');
  };

  return (
    <Parcel
      mountParcel={mountRootParcel}
      skipCSSUnmount={true}
      sessionData={getSessionData()}
      handleError={(err: Error) => datadogRum.addError(err)}
      customerId={customerId}
      creditAccountId={creditAccountId}
      fetchInvoices={fetchInvoices}
      onViewInvoice={handleViewInvoice}
      onViewSale={handleViewTransaction}
      fetchDeposits={fetchDeposits}
      onViewDeposit={handleViewTransaction}
      config={async () =>
        (await SystemJS.import<InvoicingConfig>('@lightspeed/invoicing')).default
          .CustomerInvoicesListConfig
      }
    />
  );
};

const RenderedComponent = createReactRenderer({
  component: CustomerInvoicesList,
  selector: '[data-react-root="CustomerInvoicesList"]',
});

export default CustomerInvoicesList;

history.onChangeState(
  [
    {
      name: ['customer.views.customer'],
      form_name: 'view',
      tab: 'account',
      id: undefined,
    },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
